import { buildApiUrl, buildAssetUrl } from '../../utils/api';
import { getCookie } from '../../utils/cookies';
import toast from 'react-hot-toast';

const CART_BASE = buildApiUrl('api/v1/customer/cart');
const GUEST_CART_KEY = 'kkisan_guest_cart';

// ─── Helpers ────────────────────────────────────────────────────────────────

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getCookie('authToken')}`,
});

const isLoggedIn = () => !!getCookie('authToken');

// Guest cart persistence
const getGuestCart = () => {
  try { return JSON.parse(localStorage.getItem(GUEST_CART_KEY) || '[]'); }
  catch { return []; }
};
const saveGuestCart = (cart) => localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
const clearGuestCart = () => localStorage.removeItem(GUEST_CART_KEY);

// Extract product_id from object or raw id
const getProductId = (item) =>
  typeof item === 'object' ? (item.product_id ?? item.id) : item;

// Extract selected variant from product object
const getSelectedVariant = (item = {}) =>
  (typeof item.selectedVariant === 'object' && item.selectedVariant)
    ? item.selectedVariant
    : null;

// Extract variant ids from item/product
const getVariantIds = (item = {}) => {
  const sv = getSelectedVariant(item) || {};
  return {
    product_variant_id:
      item.product_variant_id ??
      sv.product_variant_id ??
      null,
    variant_id:
      item.variant_id ??
      sv.variant_id ??
      null,
  };
};

// Check if two cart items refer to the same product+variant
const sameCartItem = (item, product_id, product_variant_id) => {
  if (String(item.product_id) !== String(product_id)) return false;
  if (product_variant_id) {
    return String(item.product_variant_id) === String(product_variant_id);
  }
  return !item.product_variant_id;
};

// Payload for POST /cart/add  →  { product_id, quantity, product_variant_id? }
const buildAddPayload = (product_id, quantity, product_variant_id) => {
  const p = { product_id: Number(product_id), quantity: Number(quantity) };
  if (product_variant_id) p.product_variant_id = Number(product_variant_id);
  return p;
};

// Payload for PATCH increment/decrement & DELETE /cart/item  →  { product_id, product_variant_id? }
const buildUpdatePayload = (product_id, product_variant_id) => {
  const p = { product_id: Number(product_id) };
  if (product_variant_id) p.product_variant_id = Number(product_variant_id);
  return p;
};

const getVariantLabel = (variant) => {
  if (!variant) return null;
  return `${variant.variant_name ?? ''} ${variant.unit_name ?? ''}`.trim() || null;
};

// Normalize a raw cart item from the API into a consistent shape
const normalizeItem = (item, fallback = null) => {
  // p is ONLY the nested product sub-object (if present), never the item itself
  const p = (item.product && typeof item.product === 'object') ? item.product : {};
  const fb = fallback ?? {};

  const product_variant_id = item.product_variant_id ?? p.product_variant_id ?? null;
  const variant_id         = item.variant_id ?? p.variant_id ?? null;

  const variantLabel = item.variant_name
    ? `${item.variant_name} ${item.unit_name ?? ''}`.trim()
    : p.variant_name
      ? `${p.variant_name} ${p.unit_name ?? ''}`.trim()
      : getVariantLabel(item.variant ?? p.variant ?? null);

  return {
    cart_item_id:       item.cart_item_id ?? item.id ?? null,
    product_id:         item.product_id ?? p.product_id ?? p.id ?? fb.product_id ?? fb.id,
    product_variant_id,
    variant_id,
    // item-level name fields take strict priority — prevents stale product store data leaking in
    name:  item.product_name ?? item.name ?? p.name ?? p.product_name ?? fb.name ?? 'Product',
    image: buildAssetUrl(
      item.image ?? item.image1 ?? item.product_image ??
      p.image ?? p.image1 ?? p.product_image ??
      fb.image ?? fb.image1 ?? null
    ),
    price: parseFloat(
      item.discount_price ?? item.variant_price ?? item.price ??
      p.discount_price ?? p.variant_price ?? p.price ??
      fb.price ?? fb.starting_price ?? 0
    ),
    originalPrice: parseFloat(
      item.original_price ?? item.originalPrice ??
      p.original_price ?? p.originalPrice ??
      fb.original_price ?? fb.originalPrice ?? 0
    ),
    gst: parseFloat(
      item.gst ?? item.gst_percentage ??
      p.gst ?? p.gst_percentage ??
      fb.gst ?? fb.gst_percentage ?? 0
    ),
    item_total: parseFloat(item.item_total ?? item.total_price ?? 0),
    quantity:   parseInt(item.quantity ?? item.qty ?? 1, 10),
    variant:    variantLabel,
  };
};

const calcCounts = (cart) => ({
  cart_count: cart.reduce((s, i) => s + (parseInt(i.quantity ?? 0, 10) || 0), 0),
});

// ─── Slice ───────────────────────────────────────────────────────────────────

export const createCartSlice = (set, get) => ({
  cart: [],
  cart_count: 0,
  cart_total: 0,
  cart_gst_total: 0,
  // Server-provided totals (populated after fetchCart)
  cart_meta: {
    subtotal: 0,
    tax_amount: 0,
    delivery_fee: 0,
    total_amount: 0,
    free_delivery_min_amount: 500,
    amount_needed_for_free_delivery: 0,
    applied_pincode: null,
  },
  cart_loading: false,
  cart_error: null,
  is_guest_cart: false,

  // ── Load guest cart from localStorage (unauthenticated) ──
  loadGuestCart: () => {
    if (isLoggedIn()) return;
    const items = getGuestCart();
    set({ cart: items, ...calcCounts(items), is_guest_cart: true });
  },

  // ── Merge guest cart items to server after login ──
  mergeGuestCartOnLogin: async () => {
    const guestItems = getGuestCart();
    if (!guestItems.length) {
      set({ is_guest_cart: false });
      return;
    }

    let anyFailed = false;
    for (const item of guestItems) {
      try {
        const res = await fetch(`${CART_BASE}/add`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(buildAddPayload(
            item.product_id,
            item.quantity,
            item.product_variant_id
          )),
        });
        const json = await res.json();
        if (!res.ok || !json.success) anyFailed = true;
      } catch {
        anyFailed = true;
      }
    }

    clearGuestCart();
    set({ is_guest_cart: false });
    await get().fetchCart();

    if (anyFailed) {
      toast.error('Some cart items could not be synced. Please re-add them.');
    } else {
      toast.success(`${guestItems.length} item(s) from your guest cart have been saved!`);
    }
  },

  // ── GET /api/v1/customer/cart ──
  fetchCart: async () => {
    if (!isLoggedIn()) {
      get().loadGuestCart();
      return;
    }

    set({ cart_loading: true, cart_error: null });
    try {
      const res = await fetch(CART_BASE, { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to fetch cart');

      const raw = json.data;
      const knownProducts = get().products || [];
      const productDetail = get().productDetail;

      const findFallback = (item) => {
        const pid = item.product_id ?? item.product?.product_id ?? item.id;
        if (productDetail && String(productDetail.product_id ?? productDetail.id) === String(pid))
          return productDetail;
        return knownProducts.find((p) =>
          String(p.id) === String(pid) || String(p.product_id) === String(pid)
        );
      };

      const items = (
        raw?.items ?? raw?.cart_items ?? raw?.cartItems ??
        (Array.isArray(raw) ? raw : [])
      ).map((item) => normalizeItem(item, findFallback(item)));

      const cart_meta = {
        subtotal:                      parseFloat(raw?.subtotal ?? 0),
        tax_amount:                    parseFloat(raw?.tax_amount ?? 0),
        delivery_fee:                  parseFloat(raw?.delivery_fee ?? 0),
        total_amount:                  parseFloat(raw?.total_amount ?? 0),
        free_delivery_min_amount:      parseFloat(raw?.free_delivery_min_amount ?? 500),
        amount_needed_for_free_delivery: parseFloat(raw?.amount_needed_for_free_delivery ?? 0),
        applied_pincode:               raw?.applied_pincode ?? null,
      };

      set({
        cart: items,
        ...calcCounts(items),
        cart_total: cart_meta.total_amount,
        cart_gst_total: cart_meta.tax_amount,
        cart_meta,
        cart_loading: false,
        is_guest_cart: false,
      });
    } catch (err) {
      set({ cart_error: err.message, cart_loading: false });
    }
  },

  // ── POST /api/v1/customer/cart/add ──
  // Body: { product_id, quantity, product_variant_id? }
  addToCart: async (productOrId, quantity = 1) => {
    const product_id = getProductId(productOrId);
    if (!product_id) return;

    const source = typeof productOrId === 'object' ? productOrId : {};
    const selectedVariant = getSelectedVariant(source);
    const { product_variant_id } = getVariantIds(source);

    // ── Guest (not logged in) ──
    if (!isLoggedIn()) {
      const guestCart = getGuestCart();
      const existing = guestCart.find((i) => sameCartItem(i, product_id, product_variant_id));
      const knownProducts = get().products || [];
      const fromStore = knownProducts.find((p) =>
        String(p.id) === String(product_id) || String(p.product_id) === String(product_id)
      );
      const product = Object.keys(source).length ? source : fromStore;

      const updated = existing
        ? guestCart.map((i) =>
            sameCartItem(i, product_id, product_variant_id)
              ? { ...i, quantity: i.quantity + quantity }
              : i
          )
        : [...guestCart, {
            product_id,
            product_variant_id: product_variant_id ?? null,
            variant_id: selectedVariant?.variant_id ?? null,
            name:  product?.name ?? product?.product_name ?? fromStore?.name ?? 'Product',
            image: product?.image ?? product?.image1 ?? fromStore?.image ?? null,
            price: parseFloat(selectedVariant?.discount_price ?? product?.price ?? fromStore?.price ?? 0),
            originalPrice: parseFloat(selectedVariant?.original_price ?? product?.originalPrice ?? fromStore?.originalPrice ?? 0),
            gst: parseFloat(product?.gst ?? product?.gst_percentage ?? fromStore?.gst ?? 0),
            quantity,
            variant: getVariantLabel(selectedVariant),
            cart_item_id: null,
          }];

      saveGuestCart(updated);
      set({ cart: updated, ...calcCounts(updated), is_guest_cart: true });
      return;
    }

    // ── Authenticated ──
    set((s) => ({ cart_count: s.cart_count + quantity, cart_loading: true, cart_error: null })); // optimistic count
    try {
      const res = await fetch(`${CART_BASE}/add`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(buildAddPayload(product_id, quantity, product_variant_id)),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to add to cart');
      await get().fetchCart();
    } catch (err) {
      set((s) => ({ cart_count: Math.max(0, s.cart_count - quantity), cart_error: err.message, cart_loading: false }));
      toast.error(err.message || 'Failed to add to cart');
    }
  },

  // ── PATCH /api/v1/customer/cart/item/increment ──
  // Body: { product_id, product_variant_id? }
  incrementItem: async (productOrItem) => {
    const product_id = getProductId(productOrItem);
    const { product_variant_id } = getVariantIds(typeof productOrItem === 'object' ? productOrItem : {});

    const updated = get().cart.map((i) =>
      sameCartItem(i, product_id, product_variant_id)
        ? { ...i, quantity: i.quantity + 1 }
        : i
    );
    set({ cart: updated, ...calcCounts(updated) });

    if (get().is_guest_cart) { saveGuestCart(updated); return; }

    try {
      const res = await fetch(`${CART_BASE}/item/increment`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(buildUpdatePayload(product_id, product_variant_id)),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to increment');
    } catch (err) {
      set({ cart_error: err.message });
      toast.error('Failed to update quantity');
      await get().fetchCart();
    }
  },

  // ── PATCH /api/v1/customer/cart/item/decrement ──
  // Body: { product_id, product_variant_id? }
  decrementItem: async (productOrItem) => {
    const product_id = getProductId(productOrItem);
    const { product_variant_id } = getVariantIds(typeof productOrItem === 'object' ? productOrItem : {});

    const cartItem = get().cart.find((i) => sameCartItem(i, product_id, product_variant_id));
    if (!cartItem) return;
    if (cartItem.quantity <= 1) { await get().removeFromCart(productOrItem); return; }

    const updated = get().cart.map((i) =>
      sameCartItem(i, product_id, product_variant_id)
        ? { ...i, quantity: i.quantity - 1 }
        : i
    );
    set({ cart: updated, ...calcCounts(updated) });

    if (get().is_guest_cart) { saveGuestCart(updated); return; }

    try {
      const res = await fetch(`${CART_BASE}/item/decrement`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(buildUpdatePayload(product_id, product_variant_id)),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to decrement');
    } catch (err) {
      set({ cart_error: err.message });
      toast.error('Failed to update quantity');
      await get().fetchCart();
    }
  },

  // ── DELETE /api/v1/customer/cart/item ──
  // Body: { product_id, product_variant_id? }
  removeFromCart: async (productOrItem) => {
    const product_id = getProductId(productOrItem);
    const { product_variant_id } = getVariantIds(typeof productOrItem === 'object' ? productOrItem : {});

    const updated = get().cart.filter((i) => !sameCartItem(i, product_id, product_variant_id));
    set({ cart: updated, ...calcCounts(updated) });

    if (get().is_guest_cart) { saveGuestCart(updated); return; }

    try {
      const res = await fetch(`${CART_BASE}/item`, {
        method: 'DELETE',
        headers: authHeaders(),
        body: JSON.stringify(buildUpdatePayload(product_id, product_variant_id)),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to remove item');
      toast.success('Item removed from cart');
    } catch (err) {
      set({ cart_error: err.message });
      toast.error('Failed to remove item');
      await get().fetchCart();
    }
  },

  // ── DELETE /api/v1/customer/cart/clear ──
  clearCart: async () => {
    set({ cart: [], cart_count: 0, cart_total: 0, cart_gst_total: 0, cart_meta: { subtotal: 0, tax_amount: 0, delivery_fee: 0, total_amount: 0, free_delivery_min_amount: 500, amount_needed_for_free_delivery: 0, applied_pincode: null } });
    if (get().is_guest_cart) { clearGuestCart(); return; }

    try {
      const res = await fetch(`${CART_BASE}/clear`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to clear cart');
    } catch (err) {
      set({ cart_error: err.message });
      toast.error('Failed to clear cart');
      await get().fetchCart();
    }
  },

  getCartTotal: () => get().cart_total,
  getCartCount: () => get().cart_count,
});
